<template>
  <!-- Small-screen filters of the river list: search and a row of chips; Région, Niveau and Classe open a bottom sheet
       (RiverFilterPanel.vue on full screen) -->
  <section class="river-filter-bar" aria-label="Filtres">
    <v-text-field
      :model-value="search"
      label="Rechercher une rivière"
      prepend-inner-icon="mdi-magnify"
      variant="solo-filled"
      flat
      rounded="pill"
      density="comfortable"
      clearable
      hide-details
      class="mx-4"
      @update:model-value="$emit('update:search', $event || '')"
    ></v-text-field>

    <div class="filter-chips">
      <v-chip
        v-for="group in groups"
        :key="group.key"
        :variant="group.count ? 'flat' : 'outlined'"
        :color="group.count ? 'primary' : undefined"
        append-icon="mdi-chevron-down"
        @click="openSheet(group.key)"
      >
        {{ group.label }}<span v-if="group.count">&nbsp;· {{ group.count }}</span>
      </v-chip>
      <v-chip
        v-for="toggle in toggles"
        :key="toggle.key"
        :variant="criteria[toggle.key] ? 'flat' : 'outlined'"
        :color="criteria[toggle.key] ? (toggle.key === 'dangersOnly' ? 'danger' : 'primary') : undefined"
        :prepend-icon="toggle.icon"
        :aria-pressed="String(Boolean(criteria[toggle.key]))"
        @click="criteria[toggle.key] = !criteria[toggle.key]"
      >
        {{ toggle.shortLabel }}
      </v-chip>
    </div>

    <div v-if="activeCount" class="d-flex align-center justify-space-between px-4 text-body-small text-medium-emphasis">
      <span>
        <strong class="text-high-emphasis">{{ countLabel }}</strong>
        · {{ activeCount }} filtre{{ activeCount > 1 ? "s" : "" }} actif{{ activeCount > 1 ? "s" : "" }}
      </span>
      <v-btn variant="text" size="small" color="primary" @click="clearAll">Effacer les filtres</v-btn>
    </div>

    <v-bottom-sheet v-model="sheetOpen" scrollable>
      <v-card v-if="sheet" :title="sheet.title" rounded="t-xl">
        <template #append>
          <v-btn icon="mdi-close" variant="text" aria-label="Fermer" @click="sheetOpen = false"></v-btn>
        </template>
        <v-card-text class="sheet-body">
          <template v-if="sheetKey === 'regions'">
            <v-checkbox
              v-for="region in regionItems"
              :key="region.name"
              v-model="criteria.regions"
              :value="region.name"
              color="primary"
              hide-details
            >
              <template #label>
                <span class="flex-grow-1">{{ region.name }}</span>
                <span class="text-body-small text-medium-emphasis ml-2">{{ region.count }}</span>
              </template>
            </v-checkbox>
          </template>
          <v-chip-group v-else-if="sheetKey === 'levels'" v-model="criteria.levels" multiple column>
            <v-chip
              v-for="level in levels"
              :key="level.value"
              :value="level.value"
              filter
              variant="outlined"
              color="primary"
              size="large"
            >
              <level-marker
            v-if="level.value !== 'unknown'"
            :status="level.value"
            :size="18"
            alt=""
            aria-hidden="true"
            class="mr-1"
          ></level-marker>
          <v-icon v-else icon="mdi-help-circle-outline" size="18" class="mr-1" aria-hidden="true"></v-icon>
          {{ level.label }}
            </v-chip>
          </v-chip-group>
          <v-chip-group v-else v-model="criteria.classes" multiple column>
            <v-chip
              v-for="item in classes"
              :key="item.class_level"
              :value="item.class_level"
              filter
              variant="outlined"
              color="primary"
              size="large"
            >
              {{ item.class_level }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn variant="outlined" @click="clearGroup">Effacer</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="flat" color="gold" @click="sheetOpen = false">Afficher {{ countLabel.toLowerCase() }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </section>
</template>

<script>
  import LevelMarker from "@/components/LevelMarker.vue";
  import { LEVEL_FILTERS, TOGGLE_FILTERS, activeFilters, clearCriteria, sectionCountLabel } from "@/utils/riverFilters";

  const GROUPS = [
    { key: "regions", label: "Région", title: "Région" },
    { key: "levels", label: "Niveau", title: "Niveau actuel" },
    { key: "classes", label: "Classe", title: "Classe" },
  ];

  export default {
    name: "RiverFilterBar",
    components: { LevelMarker },
    props: {
      // Edited in place: RiverList.vue watches it and saves it to the user's profile
      criteria: { type: Object, required: true },
      search: { type: String, default: "" },
      regions: { type: Array, default: () => [] },
      classes: { type: Array, default: () => [] },
      regionCounts: { type: Object, default: () => ({}) },
      // Sections the list shows with the current filters
      resultCount: { type: Number, default: 0 },
    },
    emits: ["update:search"],
    data() {
      return { levels: LEVEL_FILTERS, toggles: TOGGLE_FILTERS, sheetOpen: false, sheetKey: null };
    },
    computed: {
      groups() {
        return GROUPS.map((group) => ({ ...group, count: (this.criteria[group.key] || []).length }));
      },
      sheet() {
        return GROUPS.find((group) => group.key === this.sheetKey) || null;
      },
      regionItems() {
        // Regions without a published section would only give an empty list (kept while selected)
        return this.regions
          .filter(Boolean)
          .map((region) => ({ name: region.name, count: this.regionCounts[region.name] || 0 }))
          .filter((region) => region.count > 0 || this.criteria.regions.includes(region.name));
      },
      activeCount() {
        return activeFilters(this.criteria).length + (this.search ? 1 : 0);
      },
      countLabel() {
        return sectionCountLabel(this.resultCount);
      },
    },
    methods: {
      openSheet(key) {
        this.sheetKey = key;
        this.sheetOpen = true;
      },
      clearGroup() {
        this.criteria[this.sheetKey] = [];
      },
      clearAll() {
        clearCriteria(this.criteria);
        this.$emit("update:search", "");
      },
    },
  };
</script>

<style lang="scss" scoped>
  .river-filter-bar {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 0 8px;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }
  // One row that scrolls sideways
  .filter-chips {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 0 16px 2px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
    .v-chip {
      flex: 0 0 auto;
      min-height: 40px;
    }
  }
  .sheet-body {
    max-height: 60vh;
  }
  :deep(.v-checkbox .v-label) {
    width: 100%;
    opacity: 1;
  }
</style>
